import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';

import VariantFieldSet from 'bento-ordering/base/components/VariantFieldSet';
import { getQuantityFieldTotal } from 'helpers/forms/getQuantityFieldTotal';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import {
  getMenuItemNamesLightColor,
  getMenuItemNamesMediumColor
} from 'styles/helpers/getThemeStyles';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';

const QuantityButtonBlock = styled('div')({
  flex: '1',
  textAlign: 'right'
});

const QuantityModifierValue = styled(MenuItemNameTypography)({
  position: 'relative',
  display: 'inline-block',
  width: '30px',
  marginLeft: '5px',
  marginRight: '5px',
  textAlign: 'center'
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  bottom: 1,
  '& .MuiSvgIcon-root': {
    fill: getMenuItemNamesLightColor(theme)
  }
}));

const VariantQuantityContainer = styled(Box)({
  display: 'flex',
  margin: '9px 0'
});

const VariantOptionName = styled(MenuItemDescriptionTypography, {
  shouldForwardProp: muiPropForwarding('isDisabled')
})(({ isDisabled, theme }) => {
  const color = isDisabled
    ? getMenuItemNamesLightColor(theme)
    : getMenuItemNamesMediumColor(theme);
  return {
    width: '60%',
    display: 'block',
    wordWrap: 'break-word',
    marginRight: '15px',
    color
  };
});

function QuantityButtons({
  value,
  onChange,
  quantity,
  maxQty,
  slug,
  optionName,
  total,
  disabled
}) {
  const handleIncrementClick = () => {
    onChange({
      target: {
        value: { ...value, [slug]: parseInt(quantity) + 1 }
      }
    });
  };

  const handleDecrementClick = () => {
    onChange({
      target: {
        value: { ...value, [slug]: parseInt(quantity) - 1 }
      }
    });
  };

  const styles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  return (
    <QuantityButtonBlock>
      <StyledIconButton
        name="decrement"
        onClick={handleDecrementClick}
        aria-label={`decrement ${optionName} quantity`}
        data-testid="quantity-modifier-decrement-button"
        disabled={quantity === 0}
      >
        <RemoveCircleOutline data-testid="decrement-counter" />
      </StyledIconButton>
      <Box component="span">
        <QuantityModifierValue
          data-testid="quantity-modifier-value"
          {...styles}
        >
          {`${quantity}`}
        </QuantityModifierValue>
      </Box>
      <StyledIconButton
        name="increment"
        aria-label={`increment ${optionName} quantity`}
        disabled={total === maxQty || disabled}
        onClick={handleIncrementClick}
        data-testid="quantity-modifier-increment-button"
      >
        <AddCircleOutline data-testid="increment-counter" />
      </StyledIconButton>
    </QuantityButtonBlock>
  );
}

QuantityButtons.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object.isRequired,
  maxQty: PropTypes.number.isRequired,
  quantity: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  slug: PropTypes.string,
  optionName: PropTypes.string,
  disabled: PropTypes.bool,
  hasInvestorDayFeature: PropTypes.bool
};

function VariantQuantity({
  helperText = '',
  title,
  required,
  list,
  onChange,
  value,
  maxQty,
  minQty,
  disabled,
  hasInvestorDayFeature
}) {
  const styles = { variant: ITEM_DESCRIPTION_AND_PRICES };
  const total = getQuantityFieldTotal(Object.values(value));

  const getOptionName = option => {
    if (!hasInvestorDayFeature && option.price) {
      return `${option.label} ($${option.price})`;
    }
    return option.label;
  };

  return (
    <VariantFieldSet helperText={helperText} title={title} required={required}>
      {list.map(option => {
        return (
          <VariantQuantityContainer key={option.value}>
            <VariantOptionName
              isDisabled={option.disabled}
              component="span"
              {...styles}
            >
              {getOptionName(option)}
            </VariantOptionName>

            <QuantityButtons
              slug={option.value}
              optionName={getOptionName(option)}
              value={value}
              quantity={value[option.value] || 0}
              onChange={onChange}
              maxQty={maxQty}
              total={total}
              disabled={option.disabled || disabled}
            />
          </VariantQuantityContainer>
        );
      })}
    </VariantFieldSet>
  );
}

VariantQuantity.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.object,
  title: PropTypes.string,
  maxQty: PropTypes.number,
  minQty: PropTypes.number,
  required: PropTypes.number,
  helperText: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      price: PropTypes.number
    })
  ),
  isSizeSection: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  hasInvestorDayFeature: PropTypes.bool.isRequired
};

export default VariantQuantity;
